/**
 * ColorPickerField — selector de color para lotes/zonas (displayColor).
 * Paleta rápida + selector nativo; `null` = sin color elegido (el mapa usa su
 * color por defecto). Cada muestra es un botón con nombre accesible.
 */
import { makeStyles, tokens, Button } from '@fluentui/react-components';
import { useT } from '../../i18n';

/** Paleta rápida: tonos con contraste >= 3:1 contra blanco. */
export const COLOR_PRESETS = [
  '#c62828', '#d84315', '#a65f00', '#558b2f', '#2e7d32', '#00796b',
  '#0277bd', '#283593', '#6a1b9a', '#c2185b', '#6d4c41', '#546e7a',
];

const HEX = /^#[0-9a-fA-F]{6}$/;
export const isHexColor = (v: unknown): v is string => typeof v === 'string' && HEX.test(v);

const useStyles = makeStyles({
  row: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' },
  swatch: {
    width: '24px', height: '24px', borderRadius: tokens.borderRadiusCircular,
    border: `2px solid ${tokens.colorNeutralStroke1}`, cursor: 'pointer', padding: 0,
  },
  swatchSelected: { border: `3px solid ${tokens.colorNeutralForeground1}` },
  native: { width: '32px', height: '28px', padding: 0, border: 'none', background: 'none', cursor: 'pointer' },
});

export interface ColorPickerFieldProps {
  value?: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

export function ColorPickerField({ value, onChange, disabled }: ColorPickerFieldProps) {
  const s = useStyles();
  const t = useT();
  const current = isHexColor(value) ? value.toLowerCase() : null;
  return (
    <div className={s.row} role="group" aria-label={t.common.colorLabel}>
      {COLOR_PRESETS.map(c => (
        <button
          key={c}
          type="button"
          className={`${s.swatch} ${current === c ? s.swatchSelected : ''}`}
          style={{ background: c }}
          aria-label={c}
          aria-pressed={current === c}
          disabled={disabled}
          onClick={() => onChange(c)}
        />
      ))}
      <input
        type="color"
        className={s.native}
        aria-label={t.common.colorCustom}
        value={current ?? '#2e7d32'}
        disabled={disabled}
        onChange={e => onChange(e.target.value.toLowerCase())}
      />
      <Button size="small" appearance="subtle" onClick={() => onChange(null)} disabled={disabled || !current}>
        {t.common.colorClear}
      </Button>
    </div>
  );
}
